/**
 * Listing API Methods
 *
 * Provides functions for querying rental listing information from the HotPads API.
 * All requests are routed through the /hotpads-api/api proxy to bypass PerimeterX.
 *
 * Note on paths:
 * - Paths are relative to the API proxy base (/hotpads-api/api)
 * - Client/SSR requests go to: /hotpads-api/api/v2/listing/...
 * - Next.js rewrites to: /api/hotpads-api/v2/listing/...
 * - Proxy forwards to: https://hotpads-api.hotpads.com/hotpads-api/api/v2/listing/...
 */

import {
  apiClient,
  ApiError,
  type ApiServerRouting,
  isPerimeterXBlockedError,
  isRequestTimeoutError,
} from '../client/apiClient';
import { fetchCsrfToken } from '../client/fetchCsrfToken';
import type { DeepPartial, Wire } from '../wire';
import type { HpApiV2Result } from '../hpApiV2Result';
import { fetchByCoords } from './listingByCoords';
import type {
  HpBreadcrumbDto,
  HpDisplayTagDto,
  HpListingDetailResponseDto,
  HpPropertyMapDto,
} from '@zg-rentals/ts-hotpads-api-client';
import { LISTING_PHOTO_CAP } from '@/src/lib/utils/listingTransforms';
import { normalizeAmenities } from '@/src/lib/utils/normalizeAmenities';
import { hasV3ListingTag } from '@/src/lib/utils/v3ListingTags';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import type { CostsAndFees } from '@/src/types/costAndFees.type';
import type { ModelSummary } from '@/src/components/ListingCard/types';
import type { SurroundingAreaStats } from '@/src/types/listingDetails.type';

// ============================================================================
// Types
// ============================================================================

type ListingAreaBreadcrumb = {
  id?: string | number;
  legacyAreaId?: string;
  scope?: ListingDetailV3Area['scope'];
  zillowRegionId?: number;
  regionId?: number;
  resourceId?: string;
  name?: string;
  type?: string;
  uri?: string;
  uriV2?: string;
  keywords?: string;
};

export type ListingAreaSet = {
  specific?: ListingAreaBreadcrumb;
  breadcrumbs?: Array<ListingAreaBreadcrumb>;
  links?: Array<ListingAreaBreadcrumb>;
  schools?: Array<ListingDetailV3School>;
  districts?: Array<ListingAreaBreadcrumb>;
  surroundingAreaStats?: SurroundingAreaStats;
};

export type ListingPricingStats = Record<string, string>;

export type ListingUnitPriceDrop = {
  floorplanModelId: string;
  unitModelId: string;
  unitName: string;
  amount: number;
};

const LISTING_PRICING_STATS_TIMEOUT_MS = 1_500;

/**
 * Extended listing with additional runtime fields from API response
 * These fields exist in the API response but aren't in the legacy type definition
 */
export type ExtendedListing = SummaryListingDetails & {
  userItems?: SummaryListingDetails['userItemDates'];
  displayName?: string;
  has3dTour?: boolean;
  tourUrls?: Array<string>;
  tourLabels?: Array<string>;
  specialOffers?: Array<{ description?: string; endDate?: number }>;
  details?: {
    numUnits?: number | string | null;
    specialOffers?: Array<{ description?: string; endDate?: number }>;
  };
  lotIdEncoded?: string;
  modelSummary?: ModelSummary;
  areas?: ListingAreaSet;
  uriV2?: string;
  pricingStats?: ListingPricingStats;
  zillowRegionPricingStats?: ListingPricingStats;
  floorplans?: Array<{
    modelId?: string;
    units?: Array<{
      modelId?: string;
      name?: string;
      isNewUnit?: boolean;
      createdAgo?: string;
      hasPriceDropped?: boolean;
      priceDrop?: number | string | null;
    }>;
  }>;
};

/**
 * Building with nested listings (when trimResponse: false)
 * API can return buildings containing multiple unit listings
 */
export type BuildingWithListings = ExtendedListing & {
  /** Nested listings array for multi-unit buildings */
  listings?: Array<ExtendedListing>;
};

/**
 * Union type for API response buildings array
 * Can be either flat listings or nested building structures
 */
export type BuildingOrListing = BuildingWithListings;

/**
 * Query parameters for fetching listings by map coordinates.
 * Filter params are sent as individual query params to the Java API.
 */
export type FetchByCoordsParams = {
  /** Minimum latitude of map bounds */
  minLat: number;
  /** Maximum latitude of map bounds */
  maxLat: number;
  /** Minimum longitude of map bounds */
  minLon: number;
  /** Maximum longitude of map bounds */
  maxLon: number;
  /** Number of listings to return (default: 500) */
  limit?: number;
  /** Offset for pagination (default: 0). Use (page - 1) * limit */
  offset?: number;
  /** Area ID to constrain results to specific area (when border is visible) */
  areas?: string;
  /** Typed Zillow region id (single id, not a CSV), sent alongside `areas` */
  zillowRegionIds?: number;
  /** Typed Zillow school id (single id, not a CSV), sent alongside `areas` */
  zillowSchoolIds?: number;
  /** Typed Zillow school-district id (single id, not a CSV), sent alongside `areas` */
  zillowSchoolDistrictIds?: number;
  /** Search filters (beds, baths, price, etc.) */
  filter?: string;
  /** Sort order (e.g., 'price-asc', 'date-desc') */
  sortOrder?: string;
  /** Trim response to reduce payload size */
  trimResponse?: boolean;
  // --- Filter params (flat query params for byCoordsV2) ---
  lowPrice?: number;
  highPrice?: number;
  bedrooms?: string;
  bathrooms?: string;
  pets?: string;
  laundry?: string;
  amenities?: string;
  furnished?: boolean;
  propertyTypes?: string;
  minSqft?: number;
  maxSqft?: number;
  maxCreated?: string;
  listingTypes?: string;
  incomeRestricted?: boolean;
  seniorHousing?: boolean;
  studentHousing?: boolean;
  militaryHousing?: boolean;
  startOfAvailabilityDate?: string;
  endOfAvailabilityDate?: string;
  keywords?: string;
  // Additional-option names from rpjava hotpads-api listing/filter/ListingFilterField.java.
  minPhotos?: number;
  includeVaguePricing?: boolean;
  isListedByOwner?: boolean;
  acceptsSection8?: boolean;
  hasSpecialOffers?: boolean;
  isAcceptingRentalApplications?: boolean;
  orderBy?: string;
  channels?: string;
  components?: string;
  visible?: string;
  /** Request the full photo collection per listing (populates medPhotoUrls). */
  includePhotosCollection?: boolean;
};

/**
 * Response from fetchByCoords endpoint
 */
export type FetchByCoordsResponse = {
  buildings?: Array<BuildingOrListing>;
  numBuildingsAvailable?: number;
  numListingsAvailable?: number;
  numListingsIncluded?: number;
  numUnits?: number;
};

/**
 * Query parameters for fetching a listing by malone lot id.
 */
export type FetchByMaloneLotIdEncodedParams = {
  active?: boolean;
  components?: string;
  isBuilding?: boolean;
  timeout?: number;
  optional?: string;
  type?: 'unit' | 'building';
  listingTypes?: string;
  unit?: string;
};

type FetchByMaloneLotIdEncodedRequestOptions = {
  headers?: Record<string, string>;
  proxyOrigin?: string;
  serverRouting?: ApiServerRouting;
  signal?: AbortSignal;
  timeoutMs?: number;
  isExpectedError?: (error: ApiError) => boolean;
};

export type FetchByMaloneLotIdEncodedResponse = {
  breadcrumbs?: Array<HpBreadcrumbDto>;
  areas?: ListingAreaSet;
  availableUnits?: {
    rental?: Array<string | number>;
  };
  geo?: {
    lat?: number;
    lon?: number;
  };
  listing?: ExtendedListing;
  listings?: Array<ExtendedListing>;
  lotIdEncoded?: string;
  schools?: Array<{
    areaId?: number;
    areaSid?: string;
    geo?: {
      lat?: number;
      lon?: number;
    };
    highGrade?: string;
    id?: string;
    isPrivate?: boolean;
    lowGrade?: string;
    name?: string;
    rating?: number;
    resourceId?: string;
    type?: string;
    uriV2?: string;
  }>;
};

type HpApiResponse<T> = {
  data: T | null;
  session?: {
    sessionToken?: string;
    userToken?: string;
    csrfToken?: string;
    loggedIn?: boolean;
    verified?: boolean;
  } | null;
  error?: {
    reason?: string;
    message?: string;
  } | null;
};

// Derived from the generated HpListingDetailResponseDto so tsc reports any field the backend does not send.
// The adapter treats every field as optional.
type ItemOf<T> = NonNullable<T> extends ReadonlyArray<infer U> ? U : never;

type V3Response = DeepPartial<Wire<HpListingDetailResponseDto>>;
type V3Floorplan = ItemOf<V3Response['floorplans']>;

export type ListingDetailV3PhotoConfig = NonNullable<V3Response['photoConfig']>;
export type ListingDetailV3Media = NonNullable<V3Floorplan['media']>;
export type ListingDetailV3Range = NonNullable<NonNullable<V3Response['summary']>['price']>;
export type ListingDetailV3Areas = NonNullable<V3Response['areas']>;
export type ListingDetailV3Area = ItemOf<ListingDetailV3Areas['breadcrumbs']>;
export type ListingDetailV3School = ItemOf<ListingDetailV3Areas['schools']>;
export type ListingDetailV3PetPolicy = ItemOf<V3Response['petPolicies']>;
// The Engrain map needs all three fields, which the backend sends as required.
export type ListingDetailV3PropertyMap = Wire<HpPropertyMapDto>;
type V3SimilarBuilding = ItemOf<V3Response['similar']>;
type V3SimilarListing = ItemOf<V3SimilarBuilding['listings']>;
// displayTags and breadcrumbs keep their generated types because the card and JSON-LD helpers take them.
export type ListingDetailV3SimilarListing = Omit<V3SimilarListing, 'displayTags'> & {
  displayTags?: Array<HpDisplayTagDto>;
};
export type ListingDetailV3SimilarBuilding = Omit<V3SimilarBuilding, 'listings'> & {
  listings?: Array<ListingDetailV3SimilarListing>;
};

// fetchListingDetailV3WithPricingStats merges these from the v2 listing; the v3 response does not send them.
export type ListingDetailV3FloorplanUnit = ItemOf<V3Floorplan['units']> & {
  isNewUnit?: boolean;
  createdAgo?: string;
};
export type ListingDetailV3Floorplan = Omit<V3Floorplan, 'units'> & {
  units?: Array<ListingDetailV3FloorplanUnit>;
};
export type ListingDetailV3Details = NonNullable<V3Response['details']> & {
  specialOffers?: Array<{ description?: string; endDate?: number }>;
};

export type ListingDetailV3Data = Omit<
  V3Response,
  'floorplans' | 'details' | 'propertyMap' | 'similar' | 'breadcrumbs' | 'displayTags'
> & {
  floorplans?: Array<ListingDetailV3Floorplan>;
  details?: ListingDetailV3Details;
  propertyMap?: ListingDetailV3PropertyMap;
  similar?: Array<ListingDetailV3SimilarBuilding>;
  breadcrumbs?: Array<HpBreadcrumbDto>;
  displayTags?: Array<HpDisplayTagDto>;
  // HP-8964 reads this in fetchBuildingReviews. The v3 response sends the building review as
  // `buildingReview`, and only when the request sets includeBuildingReview.
  reviews?: import('./apartmentReviews').ApartmentReviews;
};

export type FetchByAliasEncodedParams = FetchByMaloneLotIdEncodedParams & {
  validate?: boolean;
};

export type FetchByAliasEncodedResponse = FetchByMaloneLotIdEncodedResponse & {
  listing?: ExtendedListing;
};

export type ListingPhotoCollection = {
  medPhotoUrl?: string;
  medPhotoUrls: Array<string>;
  photoCount?: number;
};

export type ReportListingBody = {
  alias: string;
  type: 'notAvailable' | string;
  message?: string;
};

export type ProviderListingsPortfolioResponse = {
  buildings?: Array<BuildingOrListing>;
  listings?: Array<ExtendedListing>;
};

type SimilarListingsResponseItem = ExtendedListing & {
  lotIdEncoded?: string;
  uri?: string;
  listings?: Array<ExtendedListing>;
};

export type FetchSimilarListingsByMaloneLotIdEncodedParams = {
  maloneLotIdEncoded: string;
  unit?: string;
  listingTypes?: string;
  limit?: number;
  excludeInquiredListings?: boolean;
  components?: string;
  includePhotosCollection?: boolean;
};

const SIMILAR_LISTINGS_COMPONENTS = 'amenities,basic,useritem,quality,model,photos,pricing';

// ============================================================================
// API Methods
// ============================================================================

/** The requested lot was merged into another; the listing now lives under `newLotIdEncoded`. */
export class MovedLotError extends Error {
  newLotIdEncoded: string;

  constructor(newLotIdEncoded: string) {
    super(`Listing lot moved to ${newLotIdEncoded}`);
    this.name = 'MovedLotError';
    this.newLotIdEncoded = newLotIdEncoded;
  }
}

/** The requested lot no longer resolves and the API did not provide a replacement listing. */
export class LotIdNotFoundError extends Error {
  constructor() {
    super('Listing lot not found');
    this.name = 'LotIdNotFoundError';
  }
}

/** The requested listing previously resolved but no longer has listing data. */
export class RemovedListingError extends ApiError {
  constructor(data: unknown) {
    super('Listing has been removed', { status: 404, data });
    this.name = 'RemovedListingError';
  }
}

/**
 * Fetch a listing payload by malone lot id.
 *
 * Used by PDP/HDP SSR to resolve canonical area breadcrumbs from a listing URL.
 */
export const fetchByMaloneLotIdEncoded = async (
  maloneLotIdEncoded: string,
  params?: FetchByMaloneLotIdEncodedParams,
  requestOptions?: FetchByMaloneLotIdEncodedRequestOptions,
): Promise<FetchByMaloneLotIdEncodedResponse> => {
  const response = await apiClient<{
    data?: FetchByMaloneLotIdEncodedResponse;
    status?: string;
    message?: string;
    success?: boolean;
  }>({
    path: '/v2/listing/byMaloneLotIdEncodedV2',
    queryParams: {
      components: 'all',
      timeout: 100,
      optional:
        'responsiveness,company_photo,popularity,listedby,pricing_stats,lotIdReviews,schools,company,floorplan',
      type: 'unit',
      listingTypes: 'corporate,rental,room,sublet',
      unit: '',
      ...params,
      maloneLotIdEncoded,
    },
    ...(requestOptions?.signal ? { signal: requestOptions.signal } : {}),
    ...(requestOptions?.headers ? { headers: requestOptions.headers } : {}),
    ...(requestOptions?.proxyOrigin ? { proxyOrigin: requestOptions.proxyOrigin } : {}),
    ...(requestOptions?.serverRouting ? { serverRouting: requestOptions.serverRouting } : {}),
    ...(requestOptions?.timeoutMs === undefined ? {} : { timeout: requestOptions.timeoutMs }),
    isExpectedError: requestOptions?.isExpectedError ?? isPerimeterXBlockedError,
  });
  if (response.status === 'MALONE_CHANGED' && response.message) {
    throw new MovedLotError(response.message);
  }

  if (response.status === 'LISTING_NOT_FOUND') {
    throw new ApiError('Listing not found in /v2/listing/byMaloneLotIdEncodedV2 response', {
      status: 404,
      data: response,
    });
  }

  // The legacy endpoint reports removed building lot IDs as INVALID_API_CALL.
  const isInvalidBuildingLot =
    response.status === 'INVALID_API_CALL' && params?.type === 'building' && params.isBuilding === true;
  if (!response.data && (response.status === 'OK' || response.success === true || isInvalidBuildingLot)) {
    throw new RemovedListingError(response);
  }

  if (response.status === 'LOT_ID_NOT_FOUND') {
    throw new LotIdNotFoundError();
  }

  if (!response.data) {
    throw new ApiError('Empty data in /v2/listing/byMaloneLotIdEncodedV2 response', { data: response });
  }

  return response.data;
};

export type FetchListingDetailV3Options = {
  timeout?: number;
  unit?: string | null;
  signal?: AbortSignal;
};

type FetchListingPricingStatsOptions = {
  signal?: AbortSignal;
};

export const fetchListingDetailV3 = async (
  maloneLotIdEncoded: string,
  options: FetchListingDetailV3Options = {},
): Promise<ListingDetailV3Data> => {
  const unit = options.unit?.trim() || null;
  const response = await apiClient<HpApiResponse<ListingDetailV3Data>>({
    path: '/web/v3/listings/detail',
    queryParams: {
      maloneLotIdEncoded,
      unit,
    },
    signal: options.signal,
    timeout: options.timeout,
    followApiRedirects: false,
    isExpectedError: isPerimeterXBlockedError,
  });

  if (!response.data) {
    throw new ApiError('Empty data in /web/v3/listings/detail response', { data: response });
  }

  return response.data;
};

export const fetchListingPricingStats = async (
  maloneLotIdEncoded: string,
  unit?: string | null,
  options: FetchListingPricingStatsOptions = {},
): Promise<ListingPricingStats | null> => {
  const response = await fetchByMaloneLotIdEncoded(
    maloneLotIdEncoded,
    {
      components: 'pricing',
      optional: 'pricing_stats',
      type: 'unit',
      unit: unit?.trim() || '',
    },
    {
      signal: options.signal,
      timeoutMs: LISTING_PRICING_STATS_TIMEOUT_MS,
      isExpectedError: (error) => isPerimeterXBlockedError(error) || isRequestTimeoutError(error),
    },
  );

  return response.listings?.[0]?.pricingStats ?? null;
};

export type ListingDetailV3WithPricingStats = {
  detail: ListingDetailV3Data;
  pricingStats: ListingPricingStats | null;
  zillowRegionPricingStats?: ListingPricingStats;
  priceDrops: Array<ListingUnitPriceDrop>;
};

type ListingDetailSupplement = Pick<ListingDetailV3WithPricingStats, 'pricingStats' | 'priceDrops'> & {
  zillowRegionPricingStats: ListingPricingStats | null;
  specialOffers: NonNullable<ListingDetailV3Details['specialOffers']>;
  unitMetadata: Array<{
    floorplanModelId: string;
    unitModelId: string;
    unitName: string;
    isNewUnit: boolean;
    createdAgo: string;
  }>;
};

function extractUnitPriceDrops(listing: ExtendedListing | undefined): Array<ListingUnitPriceDrop> {
  return (listing?.floorplans ?? []).flatMap((floorplan) =>
    (floorplan.units ?? []).flatMap((unit) => {
      const amount = Number(unit.priceDrop ?? 0);
      if (unit.hasPriceDropped !== true || !Number.isFinite(amount) || amount <= 0) return [];

      return [
        {
          floorplanModelId: floorplan.modelId ?? '',
          unitModelId: unit.modelId ?? '',
          unitName: unit.name ?? '',
          amount,
        },
      ];
    }),
  );
}

function extractUnitMetadata(listing: ExtendedListing | undefined): ListingDetailSupplement['unitMetadata'] {
  return (listing?.floorplans ?? []).flatMap((floorplan) =>
    (floorplan.units ?? []).flatMap((unit) => {
      if (unit.isNewUnit === undefined && unit.createdAgo === undefined) return [];

      return [
        {
          floorplanModelId: floorplan.modelId ?? '',
          unitModelId: unit.modelId ?? '',
          unitName: unit.name ?? '',
          isNewUnit: unit.isNewUnit === true,
          createdAgo: unit.createdAgo ?? '',
        },
      ];
    }),
  );
}

function addSupplementalUnitMetadata(
  detail: ListingDetailV3Data,
  unitMetadata: ListingDetailSupplement['unitMetadata'],
): ListingDetailV3Data {
  if (!detail.floorplans?.length || unitMetadata.length === 0) return detail;

  return {
    ...detail,
    floorplans: detail.floorplans.map((floorplan) => ({
      ...floorplan,
      units: floorplan.units?.map((unit) => {
        const metadata = unitMetadata.find(
          (candidate) =>
            candidate.floorplanModelId === (floorplan.modelId ?? '') &&
            (candidate.unitModelId
              ? candidate.unitModelId === (unit.modelId ?? '')
              : candidate.unitName && candidate.unitName === (unit.name ?? '')),
        );

        if (!metadata) return unit;

        return {
          ...unit,
          isNewUnit: unit.isNewUnit ?? metadata.isNewUnit,
          createdAgo: unit.createdAgo ?? metadata.createdAgo,
        };
      }),
    })),
  };
}

function extractListingDetailSupplement(response: FetchByMaloneLotIdEncodedResponse): ListingDetailSupplement {
  const listings = response.listings?.length ? response.listings : response.listing ? [response.listing] : [];
  const pricingStats = listings.reduce<ListingPricingStats | null>(
    (stats, listing) => (listing.pricingStats ? { ...stats, ...listing.pricingStats } : stats),
    null,
  );
  const zillowRegionPricingStats = listings.reduce<ListingPricingStats | null>(
    (stats, listing) =>
      listing.zillowRegionPricingStats ? { ...stats, ...listing.zillowRegionPricingStats } : stats,
    null,
  );
  const nestedSpecialOffers = listings.find((listing) => listing.details?.specialOffers?.length)?.details
    ?.specialOffers;
  const rootSpecialOffers = listings.find((listing) => listing.specialOffers?.length)?.specialOffers;

  return {
    pricingStats,
    zillowRegionPricingStats,
    priceDrops: listings.flatMap(extractUnitPriceDrops),
    specialOffers: nestedSpecialOffers ?? rootSpecialOffers ?? [],
    unitMetadata: listings.flatMap(extractUnitMetadata),
  };
}

async function fetchOptionalListingDetailSupplement(
  maloneLotIdEncoded: string,
  options: FetchListingDetailV3Options,
): Promise<ListingDetailSupplement> {
  try {
    const response = await fetchByMaloneLotIdEncoded(
      maloneLotIdEncoded,
      {
        components: 'all',
        optional: 'floorplan,pricing_stats',
        type: 'unit',
        unit: options.unit?.trim() || '',
      },
      {
        signal: options.signal,
        timeoutMs: LISTING_PRICING_STATS_TIMEOUT_MS,
        isExpectedError: (error) => isPerimeterXBlockedError(error) || isRequestTimeoutError(error),
      },
    );
    return extractListingDetailSupplement(response);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    return { pricingStats: null, zillowRegionPricingStats: null, priceDrops: [], specialOffers: [], unitMetadata: [] };
  }
}

function isIneligibleCostsAndFeesError(error: ApiError): boolean {
  if (error.status !== 500 || typeof error.responseData !== 'object' || error.responseData === null) {
    return false;
  }

  const response = error.responseData as Record<string, unknown>;
  return (
    response.status === 'SERVICE_ERROR' &&
    response.message === 'Property must be for rent' &&
    response.validationStatus === 'INVALID' &&
    response.success === false
  );
}

export const fetchCostsAndFees = async (
  listingAlias: string,
  options: { signal?: AbortSignal } = {},
): Promise<CostsAndFees> => {
  const response = await apiClient<HpApiV2Result<CostsAndFees>>({
    path: '/v2/costsAndFees/getCostsAndFees',
    queryParams: { listingAlias },
    signal: options.signal,
    isExpectedError: isIneligibleCostsAndFeesError,
  });
  if (!response.data) {
    throw new ApiError('Empty data in /v2/costsAndFees/getCostsAndFees response', { data: response });
  }
  return response.data;
};

export const fetchListingDetailV3WithPricingStats = async (
  maloneLotIdEncoded: string,
  options: FetchListingDetailV3Options = {},
): Promise<ListingDetailV3WithPricingStats> => {
  const [detail, supplement] = await Promise.all([
    fetchListingDetailV3(maloneLotIdEncoded, options),
    fetchOptionalListingDetailSupplement(maloneLotIdEncoded, options),
  ]);

  const detailWithSpecialOffers =
    detail.details?.specialOffers === undefined &&
    supplement.specialOffers.length &&
    (Boolean(detail.details?.specialOffer) || hasV3ListingTag(detail.tags, 'specialOffers'))
      ? {
          ...detail,
          details: { ...detail.details, specialOffers: supplement.specialOffers },
        }
      : detail;
  const enrichedDetail = addSupplementalUnitMetadata(detailWithSpecialOffers, supplement.unitMetadata);

  return {
    detail: enrichedDetail,
    pricingStats: supplement.pricingStats,
    ...(supplement.zillowRegionPricingStats
      ? { zillowRegionPricingStats: supplement.zillowRegionPricingStats }
      : {}),
    priceDrops: supplement.priceDrops,
  };
};

export const fetchByAliasEncoded = async (
  alias: string,
  params?: FetchByAliasEncodedParams,
): Promise<FetchByAliasEncodedResponse> => {
  const response = await apiClient<{ data?: FetchByAliasEncodedResponse }>({
    path: '/v2/listing/byAliasV2',
    queryParams: {
      components: 'all',
      timeout: 100,
      optional:
        'responsiveness,company_photo,popularity,listedby,pricing_stats,lotIdReviews,schools,company,floorplan',
      type: 'unit',
      unit: '',
      validate: false,
      ...params,
      alias,
    },
  });
  // An unknown alias comes back as a 200 envelope with no `data` at all, not an error status.
  // Resolved-empty rather than the throw `fetchByMaloneLotIdEncoded` uses, so callers can tell
  // "no such alias" from an outage.
  return response.data ?? {};
};

export const fetchListingPhotoCollectionByMaloneLotIdEncoded = async (
  maloneLotIdEncoded: string,
  params?: Pick<FetchByMaloneLotIdEncodedParams, 'listingTypes' | 'timeout' | 'type' | 'unit'>,
): Promise<ListingPhotoCollection> => {
  const response = await apiClient<{
    data: FetchByMaloneLotIdEncodedResponse & { listing?: ExtendedListing };
  }>({
    path: '/v2/listing/byMaloneLotIdEncodedV2',
    queryParams: {
      components: 'basic,photos',
      timeout: 100,
      type: 'unit',
      unit: '',
      listingTypes: 'corporate,rental,room,sublet',
      ...params,
      maloneLotIdEncoded,
    },
  });
  const firstListing = response.data?.listings?.[0] ?? response.data?.listing;
  const medPhotoUrls = firstListing?.medPhotoUrls?.length
    ? firstListing.medPhotoUrls
    : firstListing?.medPhotoUrl
      ? [firstListing.medPhotoUrl]
      : [];

  return {
    medPhotoUrl: firstListing?.medPhotoUrl ?? medPhotoUrls[0],
    medPhotoUrls: medPhotoUrls.slice(0, LISTING_PHOTO_CAP),
    photoCount: firstListing?.photoCount,
  };
};

export const fetchSimilarListingsByMaloneLotIdEncoded = async (
  params: FetchSimilarListingsByMaloneLotIdEncodedParams,
): Promise<Array<ExtendedListing>> => {
  const response = await apiClient<{ data: Array<SimilarListingsResponseItem> }>({
    path: '/v2/listing/similar/byMaloneLotIdEncodedV2',
    queryParams: {
      components: SIMILAR_LISTINGS_COMPONENTS,
      includePhotosCollection: true,
      limit: 10,
      excludeInquiredListings: false,
      ...params,
    },
  });
  return (response.data ?? []).flatMap((building) => {
    const listings =
      building.listings && building.listings.length > 0 ? building.listings : [building];

    return listings.map((listing) => {
      const amenities = normalizeAmenities(
        (listing.amenities ?? building.amenities) as unknown as
          | Record<string, unknown>
          | null
          | undefined,
      );

      return {
        ...listing,
        ...(amenities ? { amenities: amenities as unknown as ExtendedListing['amenities'] } : {}),
        geo: listing.geo ?? building.geo,
        maloneLotIdEncoded: listing.maloneLotIdEncoded ?? building.lotIdEncoded,
        uriV2: listing.uriV2 ?? listing.uriMalone ?? building.uri,
      };
    });
  });
};

const flattenPortfolioListings = (
  payload: ProviderListingsPortfolioResponse | null | undefined,
): Array<ExtendedListing> => {
  const listings = payload?.listings ?? [];
  const buildings = payload?.buildings ?? [];

  return [
    ...listings,
    ...buildings.flatMap((building) =>
      building.listings && building.listings.length > 0 ? building.listings : [building],
    ),
  ];
};

export const fetchProviderListingsPortfolio = async (
  aliasEncoded: string,
): Promise<Array<ExtendedListing>> => {
  const response = await apiClient<{ data: ProviderListingsPortfolioResponse }>({
    path: '/v2/listing/nearby/portfolioProperties',
    queryParams: {
      alias: aliasEncoded,
    },
  });

  return flattenPortfolioListings(response.data);
};

export const reportListing = async (body: ReportListingBody): Promise<void> => {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  const response = await apiClient<HpApiV2Result<boolean>>({
    method: 'PUT',
    path: '/v2/listing/report',
    body,
    headers,
  });
  if (response?.data !== true) {
    throw new ApiError(response?.message || 'hotpads-api did not accept the listing report', { data: response });
  }
};

// ============================================================================
// HomeHub Types & Endpoint
// ============================================================================

type HomeHubBuilding = {
  lotIdEncoded?: string;
  geo?: { lat: number; lon: number };
  uri?: string;
  city?: string;
  neighborhoods?: Array<string>;
  listings?: Array<SummaryListingDetails>;
};

type HomeHubCollection = {
  collectionType: string;
  title: string;
  name: string;
  resourceId?: string;
  buildingGroupDtos?: {
    buildings?: Array<HomeHubBuilding>;
  };
};

type HomeHubResponse = {
  data: Array<HomeHubCollection>;
};

/**
 * Fetch personalized homeHub collections (featured rentals).
 * Returns an array of collections, each containing building listings.
 */
export const fetchHomeHub = async (options?: {
  headers?: Record<string, string>;
  ip?: string;
  // Overrides geo-IP for visitors with no recent search (HpApiHomeHubHandler.list).
  resourceId?: string;
  userAgent?: string;
}): Promise<Array<HomeHubCollection>> => {
  const response = await apiClient<HomeHubResponse>({
    path: '/v2/homeHub',
    ...(options?.resourceId && { queryParams: { resourceId: options.resourceId } }),
    headers: options?.headers,
    ip: options?.ip,
    userAgent: options?.userAgent,
  });
  return response.data;
};

// ============================================================================
// DotMap Types & Endpoint
// ============================================================================

export type DotMapDot = {
  lat: number;
  lon: number;
  lotId: string;
};

export type FetchDotMapParams = Pick<
  FetchByCoordsParams,
  'minLat' | 'maxLat' | 'minLon' | 'maxLon'
> &
  Partial<
    Omit<
      FetchByCoordsParams,
      | 'minLat'
      | 'maxLat'
      | 'minLon'
      | 'maxLon'
      | 'areas'
      | 'limit'
      | 'offset'
      | 'components'
      | 'includePhotosCollection'
    >
  >;

/**
 * Fetch all building dots for the given viewport bounds.
 * Returns lightweight lat/lon/lotId points for the density dot layer.
 */
export const fetchDotMap = async (
  params: FetchDotMapParams,
  signal?: AbortSignal,
): Promise<Array<DotMapDot>> => {
  const response = await apiClient<{ data: { buildings: Array<DotMapDot> } }>({
    path: '/v2/listing/dotMap',
    queryParams: {
      ...params,
      limit: 16,
    },
    signal,
  });
  return response.data?.buildings ?? [];
};

/**
 * Listing API namespace
 * Provides organized access to all listing-related endpoints
 */
export const listing = {
  fetchByCoords,
  fetchByMaloneLotIdEncoded,
  fetchListingDetailV3,
  fetchListingDetailV3WithPricingStats,
  fetchListingPricingStats,
  fetchCostsAndFees,
  fetchByAliasEncoded,
  fetchListingPhotoCollectionByMaloneLotIdEncoded,
  fetchSimilarListingsByMaloneLotIdEncoded,
  fetchProviderListingsPortfolio,
  fetchDotMap,
  fetchHomeHub,
  reportListing,
};
